import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from './Modal';
import type { ProviderRequest } from '../types';

const HIDDEN_FORM_KEYS = new Set([
  'providerId',
  'providerType',
  'projectId',
  'requesterUserId',
]);

const FORM_LABEL_KEYS: Record<string, string> = {
  serviceType: 'providerRequests.formLabelServiceType',
  requestedServiceType: 'providerRequests.formLabelServiceType',
  description: 'providerRequests.formLabelDescription',
  message: 'providerRequests.formLabelMessage',
  timeframe: 'providerRequests.formLabelTimeframe',
  urgency: 'providerRequests.formLabelPriority',
  priority: 'providerRequests.formLabelPriority',
  preferredContact: 'providerRequests.formLabelPreferredContact',
  documentsAttached: 'providerRequests.formLabelDocumentsAttached',
};

type TranslateFn = (key: string, options?: Record<string, unknown>) => string;

function hasReadableValue(value: unknown) {
  if (value === null || value === undefined) return false;
  if (typeof value === 'string') return value.trim().length > 0;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === 'object') return Object.keys(value).length > 0;
  return true;
}

function formatFormLabel(key: string, t: TranslateFn) {
  const mappedKey = FORM_LABEL_KEYS[key];
  if (mappedKey) return t(mappedKey);
  return key
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .replace(/^./, (char) => char.toUpperCase());
}

const isCompanyPersona = (personaType: string | undefined) => personaType === 'company' || personaType === 'builder';

/**
 * The project's parties by role (ClickUp 869fk6kz3): the Beställare and the
 * Hantverkare they have hired, so a Beställare is never read as working for
 * the contractor. Either of them can be the sender.
 */
function requestParties(request: ProviderRequest) {
  const senderIsCustomer = request.requesterPersonaType === 'person';
  const senderIsContractor = isCompanyPersona(request.requesterPersonaType);
  const sender = request.requesterName || request.requesterEmail || '';
  return {
    // A name only: an e-mail address is not shown as the Beställare.
    customer: request.customerName || (senderIsCustomer ? request.requesterName || '' : ''),
    contractor: request.companyName || '',
    sender,
    senderIsCustomer,
    senderIsContractor,
  };
}

/** The Beställare's and the Hantverkare's parts of the request text, e.g. on "Att göra nu". */
export function requestPartiesText(request: ProviderRequest, t: TranslateFn) {
  const { customer, contractor } = requestParties(request);
  return [
    customer ? `${t('providerRequests.customerLabel')}: ${customer}` : '',
    contractor ? `${t('providerRequests.contractorLabel')}: ${contractor}` : '',
  ].filter(Boolean).join(' · ');
}

function formatFormValue(value: unknown, t: TranslateFn): string {
  if (Array.isArray(value)) {
    return value.map((item) => formatFormValue(item, t)).filter(Boolean).join(', ');
  }
  if (typeof value === 'object' && value !== null) {
    return Object.entries(value as Record<string, unknown>)
      .filter(([, nestedValue]) => hasReadableValue(nestedValue))
      .map(([nestedKey, nestedValue]) => `${formatFormLabel(nestedKey, t)}: ${formatFormValue(nestedValue, t)}`)
      .join(', ');
  }
  if (typeof value === 'boolean') return value ? t('providerRequests.yes', { defaultValue: 'Ja' }) : t('providerRequests.no', { defaultValue: 'Nej' });
  return String(value ?? '').trim();
}

function getRequestBody(request: ProviderRequest) {
  return (request.message || request.description || '').trim();
}

function getFormEntries(request: ProviderRequest, t: TranslateFn) {
  const body = getRequestBody(request);
  return Object.entries(request.formPayload ?? {})
    .filter(([key, value]) => !HIDDEN_FORM_KEYS.has(key) && hasReadableValue(value))
    .map(([key, value]) => [key, formatFormValue(value, t)] as const)
    .filter(([key, value]) => {
      if (!value) return false;
      if ((key === 'description' || key === 'message') && value === body) return false;
      if (key === 'serviceType' && value === request.serviceType) return false;
      if (key === 'timeframe' && value === request.timeframe) return false;
      return true;
    });
}

/** A lawyer's or inspector's incoming request: project, sender and content. */
export function ProviderRequestDetails({ request }: { request: ProviderRequest }) {
  const { t } = useTranslation();
  const [isExpanded, setExpanded] = useState(false);
  const toggleExpanded = () => setExpanded(open => !open);
  return (
    <div style={{ minWidth: 0 }}>
      <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
        {request.projectNumber ? (
          <span className="badge bg-light text-dark border">
            <i className="feather-folder me-1"></i>
            {t('personHome.projectTimeline.project', 'Projekt')} #{request.projectNumber}
          </span>
        ) : null}
        {request.serviceType ? (
          <span className="badge bg-light text-dark border">
            <i className="feather-tag me-1"></i>
            {request.serviceType}
          </span>
        ) : null}
      </div>
      <div className="fw-semibold text-truncate-2-line project-card-title">
        {request.projectDescription || request.serviceType || t('providerRequests.fallbackTitle', 'Ny förfrågan')}
      </div>
      {(() => {
        const { customer, contractor, sender, senderIsCustomer, senderIsContractor } = requestParties(request);
        const contact = (request.requesterPhone || request.requesterEmail) ? (
          <div className="d-flex flex-column flex-sm-row flex-wrap column-gap-3 provider-request-meta">
            {request.requesterPhone ? (
              <a href={`tel:${request.requesterPhone}`} className="provider-request-contact text-decoration-none">
                <i className="feather-phone me-1" aria-hidden="true"></i>{request.requesterPhone}
              </a>
            ) : null}
            {request.requesterEmail ? (
              <a href={`mailto:${request.requesterEmail}`} className="provider-request-contact text-decoration-none text-break">
                <i className="feather-mail me-1" aria-hidden="true"></i>{request.requesterEmail}
              </a>
            ) : null}
          </div>
        ) : null;
        const senderBadge = <span className="badge bg-primary">{t('providerRequests.senderBadge')}</span>;
        // A sender without a party box of their own (no project) still shows who sent it.
        const senderOutsideParties = !(senderIsCustomer && customer) && !(senderIsContractor && contractor);
        return (
          <div className="row g-2 mt-1" data-testid="provider-request-parties">
            {customer ? (
              <div className="col-12 col-sm-6">
                <div className="border rounded bg-light p-2 h-100" data-testid="provider-request-customer">
                  <div className="d-flex align-items-center justify-content-between gap-2 flex-wrap">
                    <span className="text-uppercase text-muted fw-semibold provider-request-meta">
                      <i className="feather-user me-1" aria-hidden="true"></i>{t('providerRequests.customerLabel')}
                    </span>
                    {senderIsCustomer ? senderBadge : null}
                  </div>
                  <div className="fw-semibold text-break">{customer}</div>
                  {senderIsCustomer ? contact : null}
                </div>
              </div>
            ) : null}
            {contractor ? (
              <div className="col-12 col-sm-6">
                <div className="border rounded bg-light p-2 h-100" data-testid="provider-request-contractor">
                  <div className="d-flex align-items-center justify-content-between gap-2 flex-wrap">
                    <span className="text-uppercase text-muted fw-semibold provider-request-meta">
                      <i className="feather-briefcase me-1" aria-hidden="true"></i>{t('providerRequests.contractorLabel')}
                    </span>
                    {senderIsContractor ? senderBadge : null}
                  </div>
                  <div className="fw-semibold text-break">{contractor}</div>
                  {senderIsContractor && sender ? (
                    <div className="small text-muted text-break provider-request-meta">{t('providerRequests.contactPerson', { name: sender })}</div>
                  ) : null}
                  {senderIsContractor ? contact : null}
                </div>
              </div>
            ) : null}
            {senderOutsideParties ? (
              <div className="col-12">
                <div className="border rounded bg-light p-2" data-testid="provider-request-sender">
                  <div className="text-uppercase text-muted fw-semibold provider-request-meta">
                    <i className="feather-send me-1" aria-hidden="true"></i>{t('providerRequests.sentBy')}
                  </div>
                  <div className="fw-semibold text-break">{sender || t('providerRequests.unknownRequester', 'Okänd avsändare')}</div>
                  {contact}
                </div>
              </div>
            ) : null}
          </div>
        );
      })()}
      {request.timeframe && (
        <div className="small text-muted mt-2 provider-request-meta">
          <i className="feather-clock me-1" aria-hidden="true"></i>
          {t('providerRequests.formLabelTimeframe')}: {t(`marketplace.request.timeframes.${request.timeframe}`, { defaultValue: request.timeframe })}
        </div>
      )}
      {(() => {
        const body = getRequestBody(request);
        const formEntries = getFormEntries(request, t);
        const hasContent = Boolean(body) || formEntries.length > 0;
        const isLong = body.length > 260 || formEntries.length > 4;
        const visibleFormEntries = isExpanded ? formEntries : formEntries.slice(0, 4);
        const visibleBody = !isExpanded && body.length > 260 ? `${body.slice(0, 260).trim()}...` : body;

        return (
          <div className="mt-3 border-top pt-3">
            <div className="small text-uppercase text-muted fw-semibold mb-2 request-section-title">
              {t('providerRequests.contentTitle', 'Innehåll i förfrågan')}
            </div>
            {body ? (
              <div className="small text-body mb-2 provider-request-meta" style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                {visibleBody}
              </div>
            ) : null}
            {visibleFormEntries.length > 0 ? (
              <dl className="row g-2 small mb-0 provider-request-meta">
                {visibleFormEntries.map(([key, value]) => (
                  <React.Fragment key={key}>
                    <dt className="col-sm-4 text-muted fw-semibold">{formatFormLabel(key, t)}</dt>
                    <dd className="col-sm-8 mb-0" style={{ overflowWrap: 'anywhere' }}>{value}</dd>
                  </React.Fragment>
                ))}
              </dl>
            ) : null}
            {!hasContent ? (
              <div className="small text-muted fst-italic provider-request-meta">
                {t('providerRequests.noContent', 'Ingen beskrivning angiven.')}
              </div>
            ) : null}
            {isLong ? (
              <button
                type="button"
                className="btn btn-link btn-sm p-0 mt-2"
                onClick={toggleExpanded}
              >
                {isExpanded
                  ? t('providerRequests.showLess', 'Visa mindre')
                  : t('providerRequests.showMore', 'Visa mer')}
              </button>
            ) : null}
          </div>
        );
      })()}
    </div>
  );
}

interface ProviderRequestModalProps {
  request: ProviderRequest;
  busy: boolean;
  error?: string;
  onRespond: (action: 'accepted' | 'declined') => void;
  onClose: () => void;
}

/** "Svara på förfrågan" from "Att göra nu": the request with Acceptera / Avslå. */
export function ProviderRequestModal({ request, busy, error, onRespond, onClose }: ProviderRequestModalProps) {
  const { t } = useTranslation();
  return (
    <Modal
      title={t('providerRequests.respondTitle', { service: request.serviceType || t('providerRequests.fallbackTitle', 'Ny förfrågan') })}
      onClose={onClose}
      fullscreenBelow="sm"
      footer={
        <>
          <button type="button" className="btn btn-outline-danger" disabled={busy} onClick={() => onRespond('declined')}>
            <i className="feather-x me-1" aria-hidden="true"></i>
            {t('providerRequests.decline', 'Avslå')}
          </button>
          <button type="button" className="btn btn-success" disabled={busy} onClick={() => onRespond('accepted')}>
            <i className="feather-check me-1" aria-hidden="true"></i>
            {t('providerRequests.accept', 'Acceptera')}
          </button>
        </>
      }
    >
      {error && <div className="alert alert-danger mb-3">{error}</div>}
      <ProviderRequestDetails request={request} />
    </Modal>
  );
}
